/**
 * AhirbhutamshaInfoTable Component
 * Displays D40 Ahirbhutamsha system for mantra practice analysis
 * 8 AHI (actions) × 5 BHUTA (elements) = 40 divisions
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack
} from '@chakra-ui/react';
import { JatakaResponse } from '../../../../../models/api';
import {
  calculateAhirbhutamshas,
  AhirbhutamshaData,
  AhirbhutamshaAttributes,
  AHI_DESCRIPTIONS,
  BHUTA_DESCRIPTIONS
} from '../data/AhirbhutamshaTableData';
import {
  getAhiColor,
  getBhutaColor
} from '../../../../../config/ahirbhutamshaColors';
import { BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor } from '../../../../../config/ashtamshaColors';
import { createPlanetDisplay } from '../helpers/planetDisplayHelper';
import {
  TABLE_WRAPPER_INLINE_STYLES,
  TABLE_CONTAINER_STYLES,
  TABLE_BASE_STYLES,
  TABLE_HEADER_ALT_STYLES,
  TABLE_ROW_HOVER_STYLES,
  TABLE_TD_BASE_STYLES,
  TABLE_TD_SMALL_STYLES,
  TABLE_TD_CENTER_STYLES,
  PLANET_TEXT_STYLES,
  EMPTY_MESSAGE_BOX_STYLES,
  EMPTY_CELL_TEXT_STYLES,
  COLUMN_STYLES
} from './styles/tableStyles';

export interface AhirbhutamshaInfoTableProps {
  apiResponse?: JatakaResponse | null;
  calculatedData?: {
    divisions: Array<{
      planet: string;
      positionInRashi: number;
      ahiNumber: number;
      ahiType: string;
      bhutaNumber: number;
      bhutaType: string;
      amshaNumber: number;
      interpretation: string;
    }>;
    calculationTime: number;
  };
}

/**
 * Group divisions by amsha number for display
 */
interface AmshaRow {
  amshaNumber: number;
  ahiType: any;
  bhutaType: any;
  planets: AhirbhutamshaAttributes[];
}

/**
 * Create all 40 amsha rows with planets grouped
 */
function groupByAmshaNumber(divisions: AhirbhutamshaAttributes[]): AmshaRow[] {
  const rows: AmshaRow[] = [];
  const planetsByAmsha: Record<number, AhirbhutamshaAttributes[]> = {};

  // Initialize all 40 amshas
  for (let i = 1; i <= 40; i++) {
    planetsByAmsha[i] = [];
  }

  // Group planets by amsha number
  divisions.forEach(div => {
    planetsByAmsha[div.amshaNumber].push(div);
  });

  // Create rows with all info
  for (let i = 1; i <= 40; i++) {
    const planetsInAmsha = planetsByAmsha[i];
    const ahiNumber = Math.floor((i - 1) / 5) + 1;
    const bhutaNumber = ((i - 1) % 5) + 1;

    const ahiTypes = ['KRTA', 'KAVACHA', 'YATRA', 'DARSHANA', 'SAMVRITI', 'BHESHAJA', 'PRASITI', 'NAYA'];
    const bhutaTypes = ['PRITHVI', 'APAS', 'AGNI', 'VAYU', 'AKASHA'];

    rows.push({
      amshaNumber: i,
      ahiType: ahiTypes[ahiNumber - 1],
      bhutaType: bhutaTypes[bhutaNumber - 1],
      planets: planetsInAmsha
    });
  }

  return rows;
}

export const AhirbhutamshaInfoTable: React.FC<AhirbhutamshaInfoTableProps> = ({ apiResponse, calculatedData }) => {
  if (!apiResponse) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>No chart data available</Text>
      </Box>
    );
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  // Use calculated data from lazy loading if available, otherwise fallback to synchronous calculation
  const ahirbhutamshaData: AhirbhutamshaData = calculatedData ?
    {
      divisions: calculatedData.divisions.map(div => ({
        planet: {
          name: div.planet,
          short_name: div.planet,
          longitude: div.positionInRashi,
          is_retrograde: false
        },
        positionInRashi: div.positionInRashi,
        ahiNumber: div.ahiNumber,
        ahiType: div.ahiType as any,
        bhutaNumber: div.bhutaNumber,
        bhutaType: div.bhutaType as any,
        amshaNumber: div.amshaNumber,
        interpretation: div.interpretation
      }))
    } :
    calculateAhirbhutamshas(apiResponse);

  if (!ahirbhutamshaData.divisions.length) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>Unable to calculate Ahirbhutamsha divisions</Text>
      </Box>
    );
  }

  // Group all divisions by amsha number (1-40)
  const amshaRows = groupByAmshaNumber(ahirbhutamshaData.divisions);

  return (
    <Box {...TABLE_WRAPPER_INLINE_STYLES}>
      {/* Main Ahirbhutamsha Table */}
      <TableContainer {...TABLE_CONTAINER_STYLES}>
        <Table {...TABLE_BASE_STYLES}>
          <Thead {...TABLE_HEADER_ALT_STYLES}>
            <Tr>
              <Th {...COLUMN_STYLES.large}>Planets</Th>
              <Th {...COLUMN_STYLES.xsmall}>Part</Th>
              <Th {...COLUMN_STYLES.medium}>AHI</Th>
              <Th {...COLUMN_STYLES.medium}>BHUTA</Th>
              <Th {...COLUMN_STYLES.xxlarge}>Interpretation</Th>
            </Tr>
          </Thead>
          <Tbody>
            {amshaRows.map((row) => {
              const ahiInfo = AHI_DESCRIPTIONS[row.ahiType as keyof typeof AHI_DESCRIPTIONS];
              const bhutaInfo = BHUTA_DESCRIPTIONS[row.bhutaType as keyof typeof BHUTA_DESCRIPTIONS];
              const ahiColor = getAhiColor(row.ahiType);
              const bhutaColor = getBhutaColor(row.bhutaType);

              return (
                <Tr key={row.amshaNumber} _hover={TABLE_ROW_HOVER_STYLES}>
                  {/* Planets column */}
                  <Td {...TABLE_TD_BASE_STYLES} minW="80px">
                    {row.planets.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap">
                        {row.planets.map((division, index) => {
                          // Use centralized planet display helper (DRY)
                          const planetDisplay = createPlanetDisplay(division.planet, currentHoraPlanet);
                          const colors = getAshtamshaPlanetColor(planetDisplay.planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                          return (
                            <Text
                              key={`${division.planet.short_name}-${index}`}
                              {...PLANET_TEXT_STYLES}
                              color={colors.color}
                              bg={colors.backgroundColor}
                              border={colors.borderColor ? `1px solid` : 'none'}
                              borderColor={colors.borderColor}
                            >
                              {planetDisplay.displayText}
                            </Text>
                          );
                        })}
                      </HStack>
                    ) : (
                      <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                    )}
                  </Td>

                  {/* Part Number */}
                  <Td {...TABLE_TD_CENTER_STYLES} fontWeight="normal">
                    <Text fontSize="xs">
                      {row.amshaNumber}
                    </Text>
                  </Td>

                  {/* AHI with color */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color={ahiColor}
                    >
                      {ahiInfo.sanskrit}
                    </Text>
                  </Td>

                  {/* BHUTA with color */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color={bhutaColor}
                    >
                      {bhutaInfo.sanskrit}
                    </Text>
                  </Td>

                  {/* Interpretation */}
                  <Td {...TABLE_TD_SMALL_STYLES} fontWeight="normal">
                    <Text fontSize="xs" color="gray.700">
                      {ahiInfo.description} through {bhutaInfo.name.toLowerCase()}
                    </Text>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
